<script setup lang="ts">
import { useRuntimeConfig } from '#app';
import { useI18n } from 'vue-i18n';
import { computed, ref, onMounted } from 'vue';
import { useAsyncData } from '#app';
import { useMenuStore } from '@/stores/menu';
import { useShopwareContext } from '@/composables/useShopwareContext';

interface NotificationItem {
  _uniqueIdentifier: string;
  translated: {
    name: string;
    description: string;
  };
  createdAt: string;
  updatedAt: string;
  name: string;
  description: string;
  active: boolean;
  id: string;
  bgColorCode: string;
  fontColorCode: string;
  startDate: string;
  endDate: string;
}

interface NotificationResponse {
  'top-bar': NotificationItem[];
  'top-promo-bar': NotificationItem[];
}

interface ApiResponse {
  data: NotificationResponse;
  status: number;
}

const { t } = useI18n();

const { data: notifications, pending, error, refresh } = useAsyncData(
  'notifications',
  async () => {
    return $fetch('/api/notifications');
  },
  { server: true }
);


const informationBannerData = computed(() => notifications.value?.['top-bar']?.[0] || null);
const promotionData = computed(() => notifications.value?.['top-promo-bar']?.[0] || null);

const menuStore = useMenuStore();

const openMobileMenu = () => {
  menuStore.isMobileMenuOpen = true;
};

const { apiClient } = useShopwareContext();
const { data: companyYearTextData } = useAsyncData('companyYearText', async () => {
  try {
    const companyYearTextResponse = await (apiClient.invoke as any)(
      "readCompanyYearText get /company-year-text",
      { pathParams: {} }
    );

    if (companyYearTextResponse && companyYearTextResponse?.data) {
      return companyYearTextResponse?.data?.companyYearText;
    }
  } catch (error) {
    console.error('Failed to load company year text:', error);
  }
  return null;
});

const companyYearText = computed(() => companyYearTextData.value);
// Optionally, expose refresh for manual use in the template

const { logoData } = useLogo();
</script>


<template>
  <div class="topbar-container">
    <LazyLayoutHeaderInformationBanner 
      v-if="informationBannerData?.translated.description"
      :informationBannerData="informationBannerData"
      :isLoading="pending"
    />
  </div>

  <header class="w-full sticky top-0 z-[40] lg:z-[50] bg-white border-b border-grey-200 lg:border-none">
    <div 
      class="benefits-bar flex flex-col text-sm text-center border-b border-grey-100"
      role="complementary"
      :aria-label="t('header.ariaLabelStoreBenefits')">

      <LazyLayoutHeaderPromotion 
      :promotionData = promotionData
      :isLoading="pending"
      :companyYearText="companyYearText"
      />
    </div>

    <NuxtLink
      to="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:p-4 focus:bg-white">
      {{ t("header.skipToText") }}
    </NuxtLink>

    <div
      class="header-inner h-[100px] sm:h-[104px] md:h-[104px] lg:h-[80px] transition-all duration-300 ease-in-out"
      role="banner">

      <div class="container">
        <div class="header-row flex flex-wrap relative justify-between items-center pt-3 lg:py-3 h-[50px] sm:h-[60px] md:h-[80px]">

          <button class="lg:hidden text-3xl" @click="openMobileMenu()" :aria-label="t('header.ariaLabelToggleMenu')" id="toggleMenu">
            <i class="icon-menu" aria-hidden="true"></i>
          </button>
          
          <div class="logo ml-4 mr-auto lg:ml-0 lg:mr-0 order-1 lg:order-1">
            <NuxtLink to="/" :aria-label="t('header.ariaLabelHome')">
              <img
                :src="logoData?.url"
                :alt="logoData?.alt ?? t('header.ariaLabelLogo')"
                :height="logoData?.height ?? 56"
                :width="logoData?.width ?? 322"
                fetchpriority="high"
                class="w-[140px] md:w-[200px] lg:w-[260px] xl:w-[322px] max-h-[40px] sm:max-h-[44px] lg:max-h-[80px] transition-all duration-300 ease-in-out"
              />
            </NuxtLink>
          </div>

          <LayoutStoreSearch />

          <div class="header-right flex items-center order-2 lg:order-3">
            
            <LazyLayoutHeaderRatingWidget class="flex sm:block absolute -top-[25px] right-0 sm:relative sm:top-auto sm:right-auto text-center" />

            <LazyLayoutHeaderUserActions />

          </div>

        </div>
      </div>
    </div>
  </header>

  <LayoutTopNavigation />

</template>
